import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowUpRight, Building2 } from 'lucide-react';
import { getProjects, getCompanies } from '../lib/api';
import { CardShell, EmptyState, LoadingScreen, PageHero, staggerContainer, staggerItem } from '../components/common/BrandElements';

const ProjectsPage = () => {
  const { t } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsRes, companiesRes] = await Promise.all([getProjects(), getCompanies()]);
        setProjects(projectsRes.data.filter(p => p.is_active));
        setCompanies(companiesRes.data);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? t(company.name_ar, company.name_en) : '';
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="projects-page">
      <PageHero
        kicker={t('مشاريعنا', 'Our Projects')}
        title={t('مشاريع بهندسة واضحة', 'Projects with clear geometry')}
        description={t('استعرض أحدث المشاريع المنفذة عبر شركات المجموعة ضمن أسلوب بصري حديث ودقيق.', 'Browse the latest projects delivered by group companies through a modern, precise visual system.')}
      />

      <section className="pb-24">
        <div className="container mx-auto px-6">
          {projects.length === 0 ? (
            <EmptyState title={t('لا توجد مشاريع حالياً', 'No projects available')} />
          ) : (
            <motion.div variants={staggerContainer} initial="hidden" animate="show" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <motion.div key={project.id} variants={staggerItem}>
                  <Link to={`/projects/${project.slug}`} className="group block" data-testid={`project-card-${project.slug}`}>
                    <CardShell>
                      <div className="relative aspect-[4/3] overflow-hidden bg-raad-mist">
                        <img
                          src={project.images?.[0] || '/assets/raad-identity-hero.png'}
                          alt={t(project.title_ar, project.title_en)}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-raad-charcoal/70 via-transparent to-transparent" />
                        <div className="absolute end-4 top-4 rounded-full bg-white/90 p-3 text-raad-charcoal shadow-soft transition-colors group-hover:bg-raad-teal group-hover:text-white">
                          <ArrowUpRight className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="p-6">
                        {project.company_id && (
                          <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-raad-mist px-3 py-1 text-xs font-black text-raad-teal">
                            <Building2 className="h-3.5 w-3.5" />
                            {getCompanyName(project.company_id)}
                          </span>
                        )}
                        <h2 className="text-2xl font-black text-raad-charcoal group-hover:text-raad-teal line-clamp-2">
                          {t(project.title_ar, project.title_en)}
                        </h2>
                        <p className="mt-3 text-sm leading-7 text-raad-graphite/65 line-clamp-3">
                          {t(project.description_ar, project.description_en)}
                        </p>
                      </div>
                    </CardShell>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
